import mapboxgl from 'mapbox-gl';
import { Region, ChurchFunding } from '../../data/types/region-types';
import { getRegionColor } from '../../data/utils/region-utils';
import { formatCurrencyShort } from '../../data/utils/money-utils';

// Update the map style to a light blue theme
export const MAP_STYLE = 'mapbox://styles/mapbox/light-v11';

export const MAPBOX_TOKEN = "pk.eyJ1IjoiaG91c2U4OGNyZWF0aXZlIiwiYSI6ImNtOGcwZ2JtNTBocmYybXNmb2lkMTVwN2cifQ.RZitXYxW_g0kFVu5NvBL9g";

// Create style for region marker
export const createRegionMarkerElement = (region: Region, selectedRegion: Region | null): HTMLDivElement => {
  const el = document.createElement('div');
  el.className = 'marker';
  el.style.width = '30px';
  el.style.height = '30px';
  el.style.borderRadius = '50%';
  el.style.backgroundColor = getRegionColor(region.church_presence);
  el.style.border = '2px solid white';
  el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
  el.style.cursor = 'pointer';
  el.style.transition = 'all 0.3s ease';
  
  if (selectedRegion && selectedRegion.region_id === region.region_id) {
    el.style.width = '40px';
    el.style.height = '40px';
    el.style.border = '3px solid white';
    el.style.zIndex = '10';
  }

  return el;
};

// Create funding label element
export const createLabelElement = (region: Region, regionName: string): HTMLDivElement => {
  const labelEl = document.createElement('div');
  labelEl.className = 'region-label';
  labelEl.style.pointerEvents = 'none'; // Prevent label from interfering with clicks
  labelEl.style.display = 'flex';
  labelEl.style.flexDirection = 'column';
  labelEl.style.alignItems = 'center';
  labelEl.style.textAlign = 'center';
  labelEl.style.color = 'white';
  labelEl.style.textShadow = '0 0 4px rgba(0, 0, 0, 0.8), 0 0 2px rgba(0, 0, 0, 1)';
  labelEl.style.fontWeight = 'bold';
  labelEl.style.width = '150px';
  labelEl.style.transform = 'translateX(-50%)';
  
  const formattedAmount = formatCurrencyShort(region.pledges);
  
  labelEl.innerHTML = `
    <div style="font-size: 22px;">${formattedAmount}</div>
    <div style="font-size: 14px;">${regionName}</div>
  `;

  return labelEl;
};

// Create church marker element
export const createChurchMarkerElement = (): HTMLDivElement => {
  const churchEl = document.createElement('div');
  churchEl.className = 'church-marker';
  churchEl.style.width = '20px';
  churchEl.style.height = '20px';
  churchEl.style.borderRadius = '50%';
  churchEl.style.backgroundColor = '#ffffff';
  churchEl.style.display = 'flex';
  churchEl.style.justifyContent = 'center';
  churchEl.style.alignItems = 'center';
  churchEl.style.border = '2px solid #3b82f6';
  churchEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
  churchEl.style.cursor = 'pointer';
  
  const iconHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-church"><path d="m18 7 4 2v11a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9l4-2"/><path d="M14 22v-4a2 2 0 0 0-4 0v4"/><path d="M18 22V5l-6-3-6 3v17"/><path d="M12 7v5"/><path d="M10 9h4"/></svg>`;
  churchEl.innerHTML = iconHTML;

  return churchEl;
};

// Format church funding HTML
export const formatChurchFundingHTML = (region: Region, churchFunding: ChurchFunding[], t: (key: string, args?: any) => string): string => {
  if (!churchFunding || churchFunding.length === 0) return '';
  
  let churchFundingHTML = `
    <div class="mt-2 pt-2 border-t border-gray-200">
      <h4 class="font-bold text-sm">${t('churchFunding')}</h4>
      <ul class="text-xs space-y-1 mt-1">
  `;
  
  churchFunding.forEach(church => {
    const fundingPercentage = Math.round((church.amount_raised / church.target_amount) * 100);
    churchFundingHTML += `
      <li>
        <div class="flex justify-between">
          <span>${church.church_name}</span>
          <span>${fundingPercentage}%</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-1.5 mt-0.5">
          <div class="bg-blue-600 h-1.5 rounded-full" style="width: ${fundingPercentage}%"></div>
        </div>
        <div class="flex justify-between text-[10px] text-gray-500">
          <span>${t('missionaries')}: ${church.missionaries_supported}</span>
          <span>${formatCurrencyShort(church.amount_raised)} / ${formatCurrencyShort(church.target_amount)}</span>
        </div>
      </li>
    `;
  });
  
  churchFundingHTML += `
      </ul>
    </div>
  `;
  
  return churchFundingHTML;
};

// Create region popup content
export const createRegionPopupContent = (
  region: Region, 
  regionName: string, 
  t: (key: string, args?: any) => string
): string => {
  const churchFundingHTML = formatChurchFundingHTML(region, region.church_funding || [], t);
  
  return `
    <div>
      <h3 class="font-bold text-lg">${regionName}</h3>
      <p class="text-sm">${t('churchPresence')}: ${t(region.church_presence.toLowerCase())}</p>
      <div class="flex justify-between text-sm mt-1">
        <span>${t('progress')}: ${Math.round((region.pledges / region.target_need) * 100)}%</span>
        <span>${formatCurrencyShort(region.pledges)} / ${formatCurrencyShort(region.target_need)}</span>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-2 mt-1">
        <div class="bg-green-600 h-2 rounded-full" style="width: ${Math.round((region.pledges / region.target_need) * 100)}%"></div>
      </div>
      <div class="flex items-center mt-2 text-sm">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        <span>${t('missionaries')}: ${region.missionaries}</span>
      </div>
      ${churchFundingHTML}
    </div>
  `;
};

// Create church popup content
export const createChurchPopupContent = (
  church: ChurchFunding,
  regionName: string,
  t: (key: string, args?: any) => string
): string => {
  const fundingPercentage = Math.round((church.amount_raised / church.target_amount) * 100);
  
  return `
    <div>
      <h4 class="font-bold">${church.church_name}</h4>
      <p class="text-sm">${t('region')}: ${regionName}</p>
      <div class="mt-1">
        <div class="flex justify-between text-xs">
          <span>${t('funding')}: ${fundingPercentage}%</span>
          <span>${formatCurrencyShort(church.amount_raised)} / ${formatCurrencyShort(church.target_amount)}</span>
        </div>
        <div class="w-full bg-gray-200 rounded-full h-1.5 mt-0.5">
          <div class="bg-blue-600 h-1.5 rounded-full" style="width: ${fundingPercentage}%"></div>
        </div>
      </div>
      <div class="flex items-center mt-1 text-xs">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-1"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        <span>${t('missionaries')}: ${church.missionaries_supported}</span>
      </div>
    </div>
  `;
};

// Calculate church coordinates with offset to prevent overlap
export const getChurchCoordinates = (
  regionCoordinates: [number, number], 
  index: number
): [number, number] => {
  const offset = 0.1 * (index + 1);
  return [
    regionCoordinates[0] + (index % 2 === 0 ? offset : -offset),
    regionCoordinates[1] + (index % 2 === 0 ? -offset : offset)
  ];
};
